import React, {
  DragEventHandler,
  DragEvent,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  memo,
  ReactNode,
} from 'react';
import clsx from 'clsx';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import {
  EditorConfig,
  Essentials,
  Paragraph,
  MultiRootEditor,
  EventInfo,
  Bold,
  Italic,
  List,
  Autoformat,
  Underline,
  TodoList,
  Strikethrough,
  RemoveFormat,
  PasteFromOffice,
  Mention,
} from 'ckeditor5';
import {
  useMultiRootEditor,
} from '@ckeditor/ckeditor5-react';
import 'ckeditor5/ckeditor5.css';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  ckeditorLicenseKey,
} from 'env';
import TextAreaToolbar, {
  ToolbarAction,
} from 'views/EMR/components/TextAreaToolbar';
import SpeechRecognitionProvider, {
  SpeechRecognitionContext,
} from 'pages/Dashboard/contexts/speechRecognition';
import {
  SpeechPhraseList,
} from 'pages/Dashboard/hooks/useSpeechToText';
import {
  EMPTY_CKE_TEXT,
  isEmptyRichText,
} from 'views/EMR/Settings/helper';
import Field from 'views/EMR/components/ckeditor-plugins/Field';
import Temporary from 'views/EMR/components/ckeditor-plugins/Temporary';
import EditorState from 'views/EMR/components/ckeditor-plugins/EditorState';
import isNil from 'lodash/isNil';
import uniqueId from 'lodash/uniqueId';

const editorPropKeys = ['onBlur', 'onReady', 'onFocus', 'onError', 'disabled', 'id'] as const;
type Part = 'root' | 'toolbar' | 'textarea';

type OnError = (
    error: Error,
    details: { phase: 'initialization' | 'runtime'; willEditorRestart?: boolean },
  ) => void;

export type Props = {
  id?: string;
  disabled?: boolean;
  onBlur?: (event: EventInfo, editor: MultiRootEditor) => void;
  onReady?: (editor: MultiRootEditor) => void;
  onFocus?: (event: EventInfo, editor: MultiRootEditor) => void;
  onError?: OnError;
}
  & Partial<EditorConfig>
  & {
    actions?: ToolbarAction[];
    onChange?: (value: string) => void;
    onEditorChange?: (value: string, editor: MultiRootEditor) => void;
    value?: string;
    classes?: Partial<Record<Part, string>>;
    trackingLabel?: string;
    phraseList?: SpeechPhraseList;
    error?: boolean;
    floatingToolbar?: boolean;
    clearContent?: boolean;
    onEditorReady?: (editor: MultiRootEditor | null) => void;
    onDragOver?: DragEventHandler<HTMLDivElement>;
};

type TextAreaProps = Omit<Props, 'disabled'>;

const defaultPlugins = [
  Essentials,
  Paragraph,
  Field,
  Temporary,
  EditorState,
  Strikethrough,
  Bold,
  Italic,
  List,
  Autoformat,
  Underline,
  TodoList,
  RemoveFormat,
  PasteFromOffice,
  Mention,
];

const defaultToolbar = [
  'bold',
  'italic',
  'underline',
  '|',
  'bulletedList',
  'numberedList',
  'todoList',
  '|',
  'removeFormat',
  '|',
  'undo',
  'redo',
];
const defaultActions: ToolbarAction[] = ['dictation'];

function TextArea({
  value,
  toolbar = defaultToolbar,
  plugins = defaultPlugins,
  actions = defaultActions,
  onChange,
  onEditorChange,
  onBlur,
  id,
  classes,
  trackingLabel,
  error,
  floatingToolbar,
  clearContent,
  onEditorReady,
  onDragOver,
  ..._props
}: TextAreaProps) {
  const onBlurRef = useRef<any>(onBlur);

  useEffect(() => {
    onBlurRef.current = onBlur;
  }, [onBlur]);

  const actionSet = new Set(actions);
  const { stopVoiceRecognition } = useContext(SpeechRecognitionContext);
  const { listening } = useContext(SpeechRecognitionContext);
  const listeningRef = useRef<boolean>(listening);
  const {
    editableElements,
    toolbarElement,
    setData,
    data,
    editor,
  } = useMultiRootEditor({
    ...pick(_props, editorPropKeys),
    onBlur: (event, editor) => {
      if (
        actionSet.has('dictation')
        && listeningRef.current
      ) {
        stopVoiceRecognition();
      }
      onBlurRef.current?.(event, editor);
    },
    onChange: (event, editor) => {
      const nextValue = editor.getData({ rootName: 'content' });
      onChange?.(nextValue);
      onEditorChange?.(nextValue, editor);
    },
    disableTwoWayDataBinding: false,
    config: {
      ...omit(_props, editorPropKeys),
      licenseKey: ckeditorLicenseKey,
      plugins,
      toolbar,
    },
    editor: MultiRootEditor,
    data: { content: value ?? '' },
  });

  useEffect(() => {
    if (!isNil(editor)) {
      onEditorReady?.(editor);
    }

    return () => { onEditorReady?.(null); };
  }, [editor, onEditorReady]);

  const contentEditorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (
      notEmptyString(value)
      && !isEmptyRichText(value)
      && data.content !== value
    ) {
      setData({ content: value ?? EMPTY_CKE_TEXT });
    }
  }, [value]);

  useEffect(() => {
    if (clearContent) {
      setData({ content: EMPTY_CKE_TEXT });
    }
  }, [clearContent]);

  useEffect(() => {
    listeningRef.current = listening;
  }, [listening]);

  const handleContentDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onDragOver?.(e);
  }, [onDragOver]);

  const handleContentDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  return (
    <div
      className={clsx(
        'populate-editor populate-field-shell w-full flex flex-col relative max-h-[320px] min-h-[120px]',
        classes?.root,
        { 'has-error': error, 'editor-with-floating-toolbar': floatingToolbar },
      )}
    >
      <div className="populate-editor-toolbar flex gap-px items-center justify-between w-full bg-gray-50 rounded-t-default-input border-b border-default-input">
        <TextAreaToolbar
          items={actions}
          editor={editor}
          id={id ?? uniqueId('text-area-toolbar-')}
          contentEditorRef={contentEditorRef as RefObject<HTMLDivElement>}
          setData={(content: string) => {
            setData({ content });
          }}
        />
        <div className="w-fit border-l border-gray-200">{toolbarElement as ReactNode}</div>
      </div>
      <div
        className={clsx(
          'populate-editor-content flex-1 w-full overflow-auto rounded-b-default-input',
          classes?.textarea,
        )}
        ref={contentEditorRef as RefObject<HTMLDivElement>}
        onDragOverCapture={!isNil(onDragOver) ? handleContentDragOver : undefined}
        onDropCapture={!isNil(onDragOver) ? handleContentDrop : undefined}
      >
        {editableElements as ReactNode}
      </div>
    </div>
  );
}

function TextAreaOutlet({
  trackingLabel,
  phraseList,
  disabled,
  ...props
}: Props) {
  return (
    <SpeechRecognitionProvider
      trackingLabel={trackingLabel}
      phraseList={phraseList}
    >
      {disabled ? (
        <div
          className="w-full flex flex-col relative max-h-[320px] min-h-[120px] overflow-hidden rounded border border-gray-200 bg-gray-50 populate-editor"
        >
          <div
            className="text-xs p-2 h-full overflow-y-auto ck-content"
            // oxlint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: props.value ?? '' }}
          />
        </div>
      ) : (
        <TextArea {...props} />
      )}
    </SpeechRecognitionProvider>
  );
}

export default memo(TextAreaOutlet);
